import {
  createBrowserRouter,
  createHashRouter,
  RouterProvider,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Typography, useTheme, Toastify } from "top-brains-ui";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Modal from "./components/modal/Modal";
import { clearUserDetails, setGlobalState, setTheme } from "./app/globalSlice";
import { clearHeaderState } from "./app/headersSlice";
import { clearTokens } from "./app/authSlice";
import { clearDraft } from "./app/addQuestionDraftSlice";
import { RootRoutes } from "./routes/RootRoutes";
import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import store, { persistor, RootState } from "./app/store";
import ReactGA from "react-ga4";
import NetworkStatusManager from "./components/network-status/NetworkStatusManager";
import {
  isElectronApp,
  getGoogleAnalyticsId,
  getAppOrigin,
} from "./utils/envUtils";

import GlobalVideoDownloader from "./components/video-downloader/GlobalVideoDownloader";
import useGlobalElectronSync from "./hooks/useGlobalElectronSync";

const isFileProtocol = window.location.protocol === "file:";
const routes = (isFileProtocol ? createHashRouter : createBrowserRouter)([
  ...RootRoutes,
]);

const GlobalChatbot = lazy(
  () => import("./components/global-chatbot/GlobalChatbot"),
);
const googleAnalyticsId = getGoogleAnalyticsId();

if (googleAnalyticsId) {
  ReactGA.initialize(googleAnalyticsId);
}

const LiveCameraPreview: React.FC<{
  stream: MediaStream | null;
  style?: React.CSSProperties;
}> = ({ stream, style }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  if (!stream) return null;

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      style={{
        width: "280px",
        height: "210px",
        borderRadius: "12px",
        border: "3px solid #ef4444",
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)",
        objectFit: "cover",
        transform: "scaleX(-1)",
        ...style,
      }}
    />
  );
};

const App: React.FC = () => {
  useGlobalElectronSync();
  const { applyTheme } = useTheme();
  const { loginStatus, theme } = useSelector(
    (state: RootState) => state.globalSlice,
  );
  const dispatch = useDispatch();

  const [currentPathname, setCurrentPathname] = useState(
    () => routes.state.location.pathname,
  );

  useEffect(() => {
    const gaId = getGoogleAnalyticsId();
    if (!gaId) return;

    if (isElectronApp()) {
      const baseOrigin = getAppOrigin();
      const cleanPath = location.pathname.startsWith("/")
        ? location.pathname
        : `/${location.pathname}`;
      const pageLocation = baseOrigin ? `${baseOrigin}${cleanPath}` : cleanPath;

      ReactGA.send({
        hitType: "pageview",
        page: cleanPath,
        page_location: pageLocation,
      });
    } else {
      ReactGA.send({
        hitType: "pageview",
        page: location.pathname,
      });
    }
  }, [location]);

  const handleLogoutClick = () => {
    persistor.purge();
    dispatch({ type: "RESET_STATE" });
    dispatch(setGlobalState({ loginStatus: "login" }));
    dispatch(clearUserDetails());
    dispatch(clearHeaderState());
    dispatch(clearTokens());
    dispatch(setTheme("app-theme"));
    setTimeout(() => {
      if (isFileProtocol) {
        window.location.hash = "#/login";
      } else {
        window.location.replace("/");
      }
    }, 100);
  };

  useEffect(() => {
    applyTheme(theme);
  }, [applyTheme, theme]);

  useEffect(() => {
    const unsubscribe = routes.subscribe((state) => {
      setCurrentPathname(state.location.pathname);
      const path = state.location.pathname;
      if (
        !path.includes("/subjects/details") &&
        !path.includes("/subjects/questions") &&
        !path.includes("/subjects/project-setup")
      ) {
        const draftState = store.getState().addQuestionDraft;
        if (
          draftState.isOpen ||
          draftState.draftValues ||
          draftState.selectedChapter
        ) {
          dispatch(clearDraft());
        }
      }
    });

    return unsubscribe;
  }, [dispatch]);

  return (
    <>
      <Toastify />
      <DndProvider backend={HTML5Backend}>
        <NetworkStatusManager currentPathname={currentPathname} />
        <RouterProvider router={routes} />
        {loginStatus === "logout" && (
          <Modal
            showSecondaryButton={false}
            showCloseButton={false}
            headerTitle="Session Expired"
            body={
              <div className="sessionModalBody">
                <Typography fontSize={16}>
                  New login session detected, Logout of current session.
                </Typography>
              </div>
            }
            height={"200px"}
            primaryButtonText="Logout"
            onPrimaryButtonClick={handleLogoutClick}
          />
        )}
        {/* <Suspense fallback={null}>
          <GlobalChatbot currentPathname={currentPathname} />
        </Suspense> */}
        <GlobalVideoDownloader />
      </DndProvider>
    </>
  );
};

export default App;
